import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { groupBy, sortBy } from 'lodash-es';
import { type Role, type ApiV1, ROLES } from '@teamworksdev/influencer/core';
import { useAuthStore } from '@/stores/authStore';
import { orderBy } from 'lodash-es';
import { getRoleName } from '@/utils/users';
import { updateLDContext } from '@/plugins/launchdarkly';
import { FEATURE_FLAGS } from '@teamworksdev/influencer/core';

export interface IDisplayProfile {
	uuid: string;
	role: Role;
	title: string;
	subtitle?: string;
	image?: string | null;
	image_alt?: string;
	avatar?: string | null;
	avatar_alt?: string;
	disabled: boolean;
	is_default: boolean;
	href?: string | null;
	adminAccessHref?: () => Promise<string | null>;
}

export function useUserProfiles() {
	const { t } = useI18n();
	const {
		profile,
		userProfiles: profiles,
	} = storeToRefs(useUserStore());
	const authStore = useAuthStore();
	const profilesSearch = ref('');

	class DisplayProfile implements IDisplayProfile {
		uuid: string;
		role: Role;
		title: string;
		subtitle?: string;
		image?: string | null;
		image_alt?: string;
		avatar?: string | null;
		avatar_alt?: string;
		disabled: boolean;
		is_default: boolean;
		href?: string | null;

		constructor(profile: ApiV1.MinimalUserProfile) {
			this.uuid = profile.uuid;
			this.role = profile.role_id;
			this.is_default = profile.is_default;
			this.title = getRoleName(profile.role_id);
			this.subtitle = '';
			this.image = null;
			this.image_alt = '';
			this.avatar = null;
			this.avatar_alt = '';
			this.disabled = false;
			this.href = null;

			switch (profile.role_id) {
				case ROLES.TEAM_USER:
					this.title = profile.data.team.name ?? '';
					this.subtitle = profile.data.group.name ?? '';
					this.image = profile.data.team.banner;
					this.image_alt = `${this.title} ${t('image')}`;
					this.avatar = profile.data.team.logo;
					this.avatar_alt = `${this.title} ${t('logo')}`;
					break;
				case ROLES.BRAND_USER:
					this.title = profile.data.exchange_brand.name ?? '';
					this.avatar = profile.data.exchange_brand.logo;
					this.avatar_alt = `${this.title} ${t('logo')}`;
					break;
				case ROLES.COMPLIANCE_OFFICER:
					this.title = profile.data.school.name ?? '';
					this.image = profile.data.school.banner;
					this.image_alt = `${this.title} ${t('image')}`;
					this.avatar = profile.data.school.logo;
					this.avatar_alt = `${this.title} ${t('logo')}`;
					break;
				case ROLES.AGENT:
					this.title = `${profile.data.athlete.first_name} ${profile.data.athlete.last_name}`;
					this.avatar = profile.data.athlete.profile_image;
					this.avatar_alt = this.title;
					this.disabled = false;
					break;
				default:
					break;
			}
		}

		async adminAccessHref() {
			const ldClient = await updateLDContext();

			if (!ldClient) throw new Error('LDClient not found');

			const isV2AdminAccessEnabled = ldClient.variation(FEATURE_FLAGS.V2_ADMIN_ACCESS, false);

			return isV2AdminAccessEnabled
				? `${import.meta.env.VITE_INFLCR_V2_BASE}/auth/swap?role=${this.role}&token=${authStore.token?.access_token}`
				: import.meta.env.VITE_INFLCR_V3_ADMIN_BASE;
		}
	}

	const displayProfiles = computed<IDisplayProfile[]>(() => {
		return orderBy(profiles.value?.map((p) => new DisplayProfile(p)), 'title', 'asc');
	});

	const profilesFiltered = computed<IDisplayProfile[]>(() => {
		return displayProfiles.value.filter((profile) => {
			const checkKeys: (keyof IDisplayProfile)[] = ['title', 'subtitle'];
			return checkKeys.some((key) => (
				(profile[key] as string)
					?.toLowerCase()
					.includes(profilesSearch.value.toLowerCase())
			));
		});
	});

	const profilesGrouped = computed<{
		displayName?: string;
		profiles: IDisplayProfile[];
		role: Role;
	}[]>(() => (
		sortBy(Object.entries(groupBy(profilesFiltered.value, 'role'))
			.map(([, profiles]) => {
				const role = profiles[0]!.role;
				return {
					displayName: getRoleName(role),
					profiles,
					role,
				};
			}), 'displayName')
	));

	const activeDisplayProfile = computed<IDisplayProfile | undefined>(() => {
		return profile && profile.value ? new DisplayProfile(profile.value) : undefined;
	});

	return {
		DisplayProfile,
		displayProfiles,
		profilesGrouped,
		profilesSearch,
		activeDisplayProfile,
	};
}
